import React, { useState, useEffect, useCallback } from "react";
import {
  FaGraduationCap,
  FaCheckCircle,
  FaTimesCircle,
  FaChartBar,
  FaTrophy,
  FaCut,
  FaTimes,
  FaUsers,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import apiRequest from "../services/api";

const ExamAnalysisModal = ({ isOpen = true, onClose, resultId }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchResultsDetails = useCallback(async () => {
    if (!resultId) return;

    setLoading(true);
    setError("");

    try {
      const res = await apiRequest(
        `users/results/public/analysis?id=${resultId}`,
        { method: "GET" }
      );

      if (res?.success && res?.data) {
        setAnalysisData(res.data);
      } else {
        setAnalysisData(null);
        setError(res?.message || "No result analysis found.");
      }
    } catch (err) {
      console.error("Failed to fetch exam analysis:", err);
      setAnalysisData(null);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [resultId]);

  useEffect(() => {
    if (isOpen && resultId) {
      fetchResultsDetails();
    }
  }, [isOpen, resultId, fetchResultsDetails]);

  if (!isOpen) return null;

  // Extracted values with safe fallbacks
  const studentName = analysisData?.student || "N/A";
  const decision = analysisData?.decision || "N/A";
  const totalMark = analysisData?.totalMark ?? 0;
  const cutScore = analysisData?.cutScore ?? 0;
  const departmentAverage = analysisData?.departmentAverage ?? 0;
  const highestScore = analysisData?.highestScore ?? 0;
  const domains = analysisData?.domains || [];

  const isPassed = decision.toUpperCase() === "PASS";

  // Custom Recharts Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-950/90 backdrop-blur-md border border-sky-500/30 p-3 rounded-xl shadow-2xl text-xs font-mono">
          <p className="font-bold text-sky-300 font-sans mb-1.5">{label}</p>
          <p className="text-cyan-400">
            Candidate's Score:{" "}
            <span className="font-bold">{payload[0]?.value}%</span>
          </p>
          <p className="text-indigo-300">
            Dept Average:{" "}
            <span className="font-bold">{payload[1]?.value}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Dark Ambient Backdrop */}
      <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-md transition-opacity" />

      {/* Modal Container */}
      <div className="relative w-full max-w-4xl bg-slate-900/60 backdrop-blur-2xl border border-sky-500/20 rounded-3xl p-6 sm:p-8 shadow-[0_0_50px_rgba(14,165,233,0.15)] text-slate-100 flex flex-col gap-6 overflow-hidden my-auto min-h-[300px] justify-center">
        {/* Glow Effects */}
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="relative flex justify-between items-start pb-4 border-b border-sky-500/10">
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-gradient-to-br from-sky-500/20 to-blue-600/20 border border-sky-500/30 rounded-2xl shadow-inner text-sky-400">
              <FaGraduationCap className="text-2xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-bold text-white capitalize tracking-wide">
                  {studentName}
                </h2>
                {!loading && !error && (
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1 border ${
                      isPassed
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                        : "bg-rose-500/10 border-rose-500/30 text-rose-400"
                    }`}
                  >
                    {isPassed ? <FaCheckCircle /> : <FaTimesCircle />}
                    {decision}
                  </span>
                )}
              </div>
              <p className="text-xs text-sky-300/60 mt-0.5 font-medium">
                Exam Domain Analysis
              </p>
            </div>
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white transition"
            >
              <FaTimes className="text-sm" />
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-sky-400">
            <FaSpinner className="animate-spin text-3xl" />
            <span className="text-sm font-medium text-slate-300">
              Fetching domain analysis...
            </span>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4">
            <FaExclamationTriangle className="text-2xl" />
            <p className="text-xs font-semibold text-center">{error}</p>
          </div>
        )}

        {/* Content State */}
        {!loading && !error && analysisData && (
          <>
            {/* Summary Metrics */}
            <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-gradient-to-b from-sky-500/10 to-transparent border border-sky-500/20 rounded-2xl p-4 flex flex-col justify-between">
                <div className="flex justify-between items-center text-sky-400 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Candidate's Score
                  </span>
                  <FaTrophy className="text-amber-400 text-sm" />
                </div>
                <div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-white font-mono">
                    {Number(totalMark).toFixed(1)}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    {" "}
                    / 100
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-b from-blue-500/10 to-transparent border border-blue-500/20 rounded-2xl p-4 flex flex-col justify-between">
                <div className="flex justify-between items-center text-blue-400 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Pass Mark
                  </span>
                  <FaCut className="text-sky-400 text-sm" />
                </div>
                <div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-slate-200 font-mono">
                    {Number(cutScore).toFixed(1)}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    {" "}
                    / 100
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-b from-indigo-500/10 to-transparent border border-indigo-500/20 rounded-2xl p-4 flex flex-col justify-between">
                <div className="flex justify-between items-center text-indigo-400 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Dept Average
                  </span>
                  <FaUsers className="text-indigo-400 text-sm" />
                </div>
                <div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-slate-200 font-mono">
                    {Number(departmentAverage).toFixed(1)}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    {" "}
                    / 100
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-b from-cyan-500/10 to-transparent border border-cyan-500/20 rounded-2xl p-4 flex flex-col justify-between">
                <div className="flex justify-between items-center text-cyan-400 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Highest Score
                  </span>
                  <FaChartBar className="text-cyan-400 text-sm" />
                </div>
                <div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-cyan-300 font-mono">
                    {Number(highestScore).toFixed(1)}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    {" "}
                    / 100
                  </span>
                </div>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="relative bg-slate-950/40 border border-sky-500/15 rounded-2xl p-4 sm:p-5 flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-bold text-sky-200 uppercase tracking-wider flex items-center gap-2">
                  <FaChartBar className="text-sky-400" /> Domain Performance
                  Breakdown
                </h3>
              </div>

              <div className="h-64 sm:h-72 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={domains}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    barGap={6}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255, 255, 255, 0.05)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: "rgba(255, 255, 255, 0.1)" }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      fontSize={11}
                      domain={[0, 100]}
                      tickLine={false}
                      axisLine={{ stroke: "rgba(255, 255, 255, 0.1)" }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{ paddingTop: "15px", fontSize: "11px" }}
                    />
                    <Bar
                      dataKey="score"
                      name="Candidate's Score"
                      fill="#06b6d4"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExamAnalysisModal;
